import { Component, OnInit, AfterViewInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { AdminSidebarComponent } from '../sidebar/admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { ManageContributionComponent } from '../../shared/components/manage-contribution/manage-contribution.component';
import { CatalogueApiService } from '@angular-workspace/services';

@Component({
  selector: 'app-admin-contributions',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    AdminSidebarComponent,
    PageHeaderComponent,
    ManageContributionComponent,
  ],
  templateUrl: './contributions.component.html',
  styleUrl: './contributions.component.scss',
})
export class AdminContributionsComponent implements OnInit, AfterViewInit {
  private catalogueApiService = inject(CatalogueApiService);
  
  @ViewChild(ManageContributionComponent) manageContributionComponent!: ManageContributionComponent;

  currentEntityType: string | undefined;
  currentSearchText: string | undefined;
  currentAssetTypes: string[] | undefined;
  currentDateRange: { start: Date | null; end: Date | null } | null = null;

  async ngOnInit(): Promise<void> {
    // Data loading moved to ngAfterViewInit to ensure ViewChild is available
  }

  async ngAfterViewInit(): Promise<void> {
    // Wait a bit to ensure ViewChild is available
    setTimeout(async () => {
      if (this.manageContributionComponent) {
        await this.loadAllAssets();
      } else {
        console.warn('manageContributionComponent not available in ngAfterViewInit, retrying...');
        // Retry after a short delay
        setTimeout(async () => {
          if (this.manageContributionComponent) {
            await this.loadAllAssets();
          } else {
            console.error('manageContributionComponent still not available after retry');
          }
        }, 100);
      }
    }, 0);
  }

  /**
   * Map display asset type to entityType for API
   * @param assetType Display asset type (e.g., "Dataset", "Model", "Usecase")
   * @returns Entity type for API (e.g., "DATABANK", "AIMODEL", "APPS")
   */
  mapAssetTypeToEntityType(assetType: string): string {
    const typeMap: Record<string, string> = {
      'Dataset': 'DATABANK',
      'Model': 'AIMODEL',
      'Models': 'AIMODEL',
      'Usecase': 'APPS',
      'Use Case': 'APPS',
      'App': 'APPS',
      'Apps': 'APPS'
    };
    return typeMap[assetType] || assetType.toUpperCase();
  }

  /**
   * Convert DateRange from filter to API format
   * @param dateRange DateRange from filter (with startDate/endDate)
   * @returns Date range in API format (with start/end)
   */
  convertDateRange(dateRange: any): { start: Date | null; end: Date | null } | null {
    if (!dateRange || (!dateRange.startDate && !dateRange.endDate)) {
      return null;
    }
    return {
      start: dateRange.startDate || null,
      end: dateRange.endDate || null
    };
  }

  /**
   * Handle filter changes from manage-contribution component
   * @param filterPayload Filter payload from the component (can be array or object)
   */
  onFilterChange(filterPayload: any[] | any): void {
    // Extract search text, asset types, date range, and entityType.
    // IMPORTANT: `ManageContributionComponent` emits the real search query as `filterPayload.q`,
    // while `filterFields['search'].value` may not be updated during typing (to avoid losing focus).
    // So we prefer `filterPayload.q` over reading `filterFields` for search.
    let searchText: string | undefined = undefined;
    let assetTypes: string[] | undefined = undefined;
    let dateRange: { start: Date | null; end: Date | null } | null = null;
    let entityType: string | undefined = undefined;

    // Prefer POST search payload `q` if available.
    if (filterPayload && typeof filterPayload === 'object' && !Array.isArray(filterPayload)) {
      const q = (filterPayload as any).q;
      if (typeof q === 'string' && q.trim() !== '') {
        searchText = q.trim();
      }
    }
    
    // Get values directly from filterFields
    if (this.manageContributionComponent && this.manageContributionComponent.filterFields) {
      const searchField = this.manageContributionComponent.filterFields.find(
        field => field.key === 'search'
      );
      const assetTypeField = this.manageContributionComponent.filterFields.find(
        field => field.key === 'assetType'
      );
      const dateRangeField = this.manageContributionComponent.filterFields.find(
        field => field.key === 'dateRange'
      );
      
      // Extract search text
      // Only fall back to filterFields when payload didn't provide `q`.
      if (
        !searchText &&
        searchField &&
        searchField.value &&
        typeof searchField.value === 'string' &&
        searchField.value.trim() !== ''
      ) {
        searchText = searchField.value.trim();
      }
      
      // Extract asset types
      if (assetTypeField && assetTypeField.value && Array.isArray(assetTypeField.value) && assetTypeField.value.length > 0) {
        assetTypes = assetTypeField.value as string[];
        // Map to entityType (use first selected type for entityType query param)
        const selectedType = assetTypes[0];
        entityType = this.mapAssetTypeToEntityType(selectedType);
      }
      
      // Extract date range
      if (dateRangeField && dateRangeField.value) {
        dateRange = this.convertDateRange(dateRangeField.value);
      }
    }
    
    // Update current values
    this.currentSearchText = searchText;
    this.currentAssetTypes = assetTypes;
    this.currentDateRange = dateRange;
    this.currentEntityType = entityType;
    
    // Load assets with updated filters
    this.loadAllAssets();
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    this.loadAllAssets();
  }

  async loadAllAssets(): Promise<void> {
    console.log('loadAllAssets called');
    if (this.manageContributionComponent) {
      this.manageContributionComponent.loading = true;
    }
    try {
      let assetsData: any;
      
      // If search text, asset types, or date range is provided, use getAllAssets with POST
      if (this.currentSearchText || this.currentAssetTypes || this.currentDateRange) {
        console.log('Using POST getAllAssets with filters:', {
          search: this.currentSearchText,
          assetTypes: this.currentAssetTypes,
          dateRange: this.currentDateRange
        });
        // When date range is selected, always include publishStatus: "ACTIVE"
        const statuses = this.currentDateRange ? ['ACTIVE'] : undefined;
        const page = this.manageContributionComponent?.pagination?.currentPage ?? 1;
        const sort = this.manageContributionComponent?.getSortString?.() ?? 'itemCreatedAt:desc';
        
        assetsData = await this.catalogueApiService.getAllAssets(
          page,
          10, // size
          this.currentSearchText, // search
          this.currentAssetTypes, // assetTypes
          statuses, // statuses - always "ACTIVE" when date range is selected
          this.currentDateRange, // dateRange
          sort
        );
      } else {
        console.log('Using GET getAllAssetsGet without filters');
        // No filters, use getAllAssetsGet with entityType if available
        const page = this.manageContributionComponent?.pagination?.currentPage ?? 1;
        const sort = this.manageContributionComponent?.getSortString?.() ?? 'itemCreatedAt:desc';
        assetsData = await this.catalogueApiService.getAllAssetsGet(
          page,
          10, // size
          sort,
          this.currentEntityType // entityType
        );
      }
      
      console.log('All assets loaded:', assetsData);
      
      // Map the response to the table
      if (this.manageContributionComponent) {
        console.log('Calling mapMyAssetsResponse');
        this.manageContributionComponent.mapMyAssetsResponse(assetsData);
      } else {
        console.warn('manageContributionComponent is not available in loadAllAssets');
      }
    } catch (error) {
      console.error('Error loading all assets:', error);
      // Ensure loading state is reset even on error
      if (this.manageContributionComponent) {
        this.manageContributionComponent.loading = false;
      }
    }
  }
}

